import { type FC, useState } from "react"
import styled from "styled-components"

import { type CancelReasonId, cancelReasonIds, cancelReasons } from "@forento/core/models/payment"
import { exhaustiveGuard } from "@forento/core/utilities/switch"
import Tooltip from "@forento/ui/components/Tooltip"

import Button from "~/components/Button"
import { InputTextArea } from "~/components/InputField"
import { DefaultModal, ModalButtons, ModalInputs, ModalPadding, ModalText, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import trpc from "~/utilities/trpc"

type Props = { type: "plan" | "addon"; isOpen: boolean; onClose(): void; onSubmitted(): Promise<void> }
const CancelSubscriptionModal: FC<Props> = ({ type, isOpen, onClose, onSubmitted }) => {
	const alert = useAlert()

	const [selectedReasonId, setSelectedReasonId] = useState<CancelReasonId>()
	const [elaboration, setElaboration] = useState("")
	const [isSubmitting, setSubmitting] = useState(false)

	async function handleSubmit() {
		if (!selectedReasonId) return
		setSubmitting(true)
		try {
			if (type === "plan") {
				await trpc.payment.creator.cancelPlan.mutate({ reason: selectedReasonId, elaboration })
			} else if (type === "addon") {
				await trpc.payment.creator.cancelAddon.mutate({ reason: selectedReasonId, elaboration })
			} else {
				exhaustiveGuard(type)
			}
			await onSubmitted()
			setSelectedReasonId(undefined)
			setElaboration("")
		} catch (error) {
			setSubmitting(false)
			console.error(error)
			await alert.show("Error", "Failed to cancel subscription. Please try again.")
		}
		setSubmitting(false)
	}

	return (
		<DefaultModal onClose={onClose} isOpen={isOpen}>
			<ModalTitle>Cancel subscription</ModalTitle>
			{!selectedReasonId ? (
				<>
					<ModalText>
						We are sad to hear you want to cancel your subscription. Please let us know why before canceling
						so we can improve the product in the future.
					</ModalText>
					<Reasons>
						{Object.entries(cancelReasons).map(([id, reason]) =>
							!(type === "addon" && id === "ltd") ? (
								<Button
									key={id}
									variant="secondary"
									onClick={() => setSelectedReasonId(cancelReasonIds.find(x => x === id))}
									className="w-full"
								>
									{reason.label}
								</Button>
							) : null,
						)}
						<Button variant="primary" onClick={onClose}>
							Keep subscription
						</Button>
					</Reasons>
				</>
			) : (
				<>
					<ModalInputs>
						<InputTextArea
							label={cancelReasons[selectedReasonId].elaboration}
							value={elaboration}
							onChange={setElaboration}
						/>
					</ModalInputs>
					<ModalButtons>
						<Button variant="secondary" onClick={() => setSelectedReasonId(undefined)}>
							Previous
						</Button>
						<Button
							variant="secondary"
							onClick={() => {
								onClose()
								setSelectedReasonId(undefined)
							}}
						>
							Keep subscription
						</Button>
						{elaboration.trim().length > 0 ? (
							<Button variant="primary-danger" onClick={handleSubmit} isLoading={isSubmitting}>
								Cancel subscription
							</Button>
						) : (
							<Tooltip tooltip="Please let us know why first">
								<Button variant="primary-danger" isDisabled>
									Cancel subscription
								</Button>
							</Tooltip>
						)}
					</ModalButtons>
				</>
			)}
		</DefaultModal>
	)
}

const Reasons = styled(ModalPadding)`
	display: flex;
	flex-direction: column;
	gap: 8px;
`

export default CancelSubscriptionModal
